/**
 * SessionStorage utilities for persisting state across page refreshes
 */

import { ProductivityReport } from './api';

const STORAGE_PREFIX = 'mbd_productivity_';

interface AppState {
  goals: string[];
  questions?: Array<{
    id: string;
    text: string;
    type: 'text' | 'choice' | 'multiple' | 'scale';
    options?: string[];
  }>;
  answers: Record<string, string>;
  email?: string;
  consentMarketing?: boolean;
  userId?: string;
  reportId?: string;
  report?: ProductivityReport;
  currentStep?: 'landing' | 'goals' | 'questions' | 'email' | 'report';
}

export const storage = {
  getState(): Partial<AppState> {
    try {
      const stored = sessionStorage.getItem(`${STORAGE_PREFIX}state`);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to load state from sessionStorage:', error);
      return {};
    }
  },

  setState(state: Partial<AppState>): void {
    try {
      const current = this.getState();
      const updated = { ...current, ...state };
      sessionStorage.setItem(`${STORAGE_PREFIX}state`, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save state to sessionStorage:', error);
    }
  },

  clearState(): void {
    try {
      sessionStorage.removeItem(`${STORAGE_PREFIX}state`);
    } catch (error) {
      console.error('Failed to clear state from sessionStorage:', error);
    }
  },

  // Individual getters for convenience
  getGoals(): string[] {
    return this.getState().goals || [];
  },

  getQuestions() {
    return this.getState().questions;
  },

  getAnswers(): Record<string, string> {
    return this.getState().answers || {};
  },

  getEmail(): string | undefined {
    return this.getState().email;
  },

  getUserId(): string | undefined {
    return this.getState().userId;
  },

  getReportId(): string | undefined {
    return this.getState().reportId;
  },

  getReport(): ProductivityReport | undefined {
    return this.getState().report;
  },

  getCurrentStep(): AppState['currentStep'] {
    return this.getState().currentStep || 'landing';
  },

  // Individual setters for convenience
  setGoals(goals: string[]): void {
    this.setState({ goals });
  },

  setQuestions(questions: AppState['questions']): void {
    this.setState({ questions });
  },

  setAnswers(answers: Record<string, string>): void {
    this.setState({ answers });
  },

  setEmail(email: string, consentMarketing: boolean): void {
    this.setState({ email, consentMarketing });
  },

  setUserId(userId: string): void {
    this.setState({ userId });
  },

  setReportId(reportId: string): void {
    this.setState({ reportId });
  },

  setReport(report: ProductivityReport): void {
    this.setState({ report });
  },

  setCurrentStep(currentStep: AppState['currentStep']): void {
    this.setState({ currentStep });
  },
};
